import { FetchResult, useMutation } from "@apollo/client";
import { ApolloError } from "@apollo/client/errors";

import {
  DASHBOARDS,
  DELETE_DASHBOARD,
  WORKSPACE,
  WORKSPACES,
  WorkspaceClientName
} from "api/workspace";

import { Workspace } from "models/workspace";

import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";
import { useWorkspaceDispatch } from "components/workspace/hooks/useWorkspaceDispatch";

import { switchActiveDashboardIfNeeded } from "../utils/switchActiveDashboardIfNeeded";

/**
 * Function to delete a dashboard globally
 * @param dashboardId - ID of the dashboard to delete
 * @param workspace - Target workspace for refetch queries and active dashboard switching logic
 */
type DeleteDashboardMutate = (
  dashboardId: string,
  workspace: Workspace
) => Promise<FetchResult>;

export type UseDeleteDashboardReturnT = {
  mutate: DeleteDashboardMutate;
  isLoading: boolean;
};

export function useDeleteDashboard({
  onCompleted,
  onError
}: {
  onCompleted?: () => void;
  onError?: (error: ApolloError) => void;
} = {}): UseDeleteDashboardReturnT {
  const { workspace, activeDashboardId } = useWorkspaceContext();
  const workspaceDispatch = useWorkspaceDispatch();
  const [deleteDashboardMutation, { loading }] = useMutation(DELETE_DASHBOARD);

  async function mutate(dashboardId: string, targetWorkspace: Workspace) {
    return await deleteDashboardMutation({
      variables: { id: dashboardId },
      context: { clientName: WorkspaceClientName },
      awaitRefetchQueries: true,
      refetchQueries: [
        { query: WORKSPACES, context: { clientName: WorkspaceClientName } },
        {
          query: WORKSPACE,
          variables: { id: targetWorkspace.workspaceId },
          context: { clientName: WorkspaceClientName }
        },
        { query: DASHBOARDS, context: { clientName: WorkspaceClientName } }
      ],
      onCompleted: () => {
        // Check if the deleted dashboard is the currently selected one
        // Only switch if we're operating on the current workspace context
        if (targetWorkspace.workspaceId === workspace?.workspaceId) {
          switchActiveDashboardIfNeeded(
            targetWorkspace,
            activeDashboardId,
            dashboardId,
            workspaceDispatch
          );
        }
        onCompleted?.();
      },
      onError: (error: ApolloError) => {
        // eslint-disable-next-line no-console
        console.error(error.message, error);
        onError?.(error);
      }
    });
  }

  return { mutate, isLoading: loading };
}
